import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  entregaService,
  EntregaResumoFilters,
  EntregaFilters,
} from "../services/entregaService";
import { useGlobalStore } from "../store/globalStore";
import { getErrorMessage } from "../utils/errorHandler";

export const useEntregaResumo = (filters: EntregaResumoFilters) => {
  return useQuery({
    queryKey: ["entrega-resumo", filters],
    queryFn: () => entregaService.getResumo(filters),
  });
};

export const useEntregaUsuarios = (filters: EntregaResumoFilters) => {
  return useQuery({
    queryKey: ["entrega-usuarios", filters],
    queryFn: () => entregaService.getUsuarios(filters),
  });
};

export const useEntregasAtivasOperacao = (
  lojaId?: number,
  options?: { refetchInterval?: number | false; refetchOnWindowFocus?: boolean }
) => {
  return useQuery({
    queryKey: ["entregas-ativas-operacao", lojaId],
    queryFn: () => entregaService.getAtivasOperacao(lojaId),
    enabled: lojaId != null,
    refetchInterval: options?.refetchInterval,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
  });
};

export const useEntregas = (
  filters?: EntregaFilters,
  options?: { refetchInterval?: number; refetchOnWindowFocus?: boolean }
) => {
  return useQuery({
    queryKey: ["entregas", filters],
    queryFn: () => entregaService.getAll(filters || {}),
    ...options,
  });
};

export const useUpdateEntrega = () => {
  const queryClient = useQueryClient();
  const { setError } = useGlobalStore();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: { pedidosDisplayIds?: number[]; quantidadeEnderecos?: number };
    }) => entregaService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entregas"] });
      queryClient.invalidateQueries({ queryKey: ["rankingDiaEntrega"] });
      queryClient.invalidateQueries({ queryKey: ["aguardando-entrega"] });
    },
    onError: (error) => setError(getErrorMessage(error)),
  });
};

export const useFinalizarEntrega = () => {
  const queryClient = useQueryClient();
  const { setError } = useGlobalStore();

  return useMutation({
    mutationFn: (id: number) => entregaService.finalizar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entregas"] });
      queryClient.invalidateQueries({ queryKey: ["entregas-ativas-operacao"] });
      queryClient.invalidateQueries({ queryKey: ["rankingDiaEntrega"] });
      queryClient.invalidateQueries({ queryKey: ["aguardando-entrega"] });
      queryClient.invalidateQueries({ queryKey: ["filaEntregas"] });
      queryClient.invalidateQueries({ queryKey: ["roteirizacao-lotes"] });
    },
    onError: (error) => setError(getErrorMessage(error)),
  });
};

export const useDeleteEntrega = () => {
  const queryClient = useQueryClient();
  const { setError } = useGlobalStore();

  return useMutation({
    mutationFn: (id: number) => entregaService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entregas"] });
      queryClient.invalidateQueries({ queryKey: ["entregas-historicos"] });
    },
    onError: (error) => setError(getErrorMessage(error)),
  });
};
